import Tooltip from '@mui/material/Tooltip'
import cn from 'classnames'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'

export type NoteAuthorshipDisplay = {
  noteCreatedAt?: string | null
  noteCreatedByName?: string | null
  noteLastEditedAt?: string | null
  noteLastEditedByName?: string | null
}

interface PremiumFollowNoteProps {
  dataKey?: string
  className?: string
  note: string | null
  showAddNote: boolean
  handleNoteAction: (note: string, updateType: 'AddNote' | 'RemoveNote') => void
  setEditMode?: (editMode: boolean) => void
  editMode?: boolean
  noteAuthorship?: NoteAuthorshipDisplay | null
}

const NOTE_AUTH_DATE_FORMAT = 'MM/DD/YYYY'

function formatNoteAuthorshipDate(value: string | null | undefined): string {
  if (!value) return ''
  const parsed = moment.utc(value)
  return parsed.isValid() ? parsed.format(NOTE_AUTH_DATE_FORMAT) : ''
}

function buildNoteAuthorshipTooltip(authorship: NoteAuthorshipDisplay | null | undefined): string | null {
  if (!authorship) return null

  const editedAt = authorship.noteLastEditedAt
  const editedBy = authorship.noteLastEditedByName?.trim()
  if (editedAt && editedBy) {
    const date = formatNoteAuthorshipDate(editedAt)
    if (!date) return null
    return `Last edited by: ${editedBy} on ${date}`
  }

  const createdAt = authorship.noteCreatedAt
  const createdBy = authorship.noteCreatedByName?.trim()
  if (createdAt && createdBy) {
    const date = formatNoteAuthorshipDate(createdAt)
    if (!date) return null
    return `Added by: ${createdBy} on ${date}`
  }

  return null
}

/**
 * Primary UI component for user interaction
 */
export const PremiumFollowNote: React.FC<PremiumFollowNoteProps> = ({
  dataKey,
  className,
  note,
  showAddNote,
  handleNoteAction = () => {},
  setEditMode = () => {},
  editMode = false,
  noteAuthorship = null,
}) => {
  const TruncateTextLength = 200
  const [showMore, setShowMore] = useState(false)
  const [noteText, setNoteText] = useState<string | null>(null)

  const authorshipTooltip = useMemo(() => buildNoteAuthorshipTooltip(noteAuthorship), [noteAuthorship])

  useEffect(() => {
    if (showAddNote) {
      setEditMode(true)
      setNoteText(note || '')
    }
  }, [showAddNote])

  const handleNoteText = (noteValue: string) => {
    const truncated =
      noteValue && noteValue.length > TruncateTextLength ? noteValue?.slice(0, TruncateTextLength) + '...' : noteValue
    setNoteText(truncated || '')
  }

  useEffect(() => {
    handleNoteText(note || '')
  }, [note])

  const toggleShowMore = () => {
    setShowMore(!showMore)
    if (!showMore) {
      setNoteText(note || '')
    } else {
      setNoteText(note?.slice(0, TruncateTextLength) + '...')
    }
  }

  const handleSaveNoteClick = () => {
    setEditMode(false)
    handleNoteAction?.(noteText || '', 'AddNote')
  }

  const handleDeleteNoteClick = () => {
    setNoteText('')
  }

  return (
    <div className={cn('relative flex', className)}>
      {editMode ? (
        <>
          <div
            className={`flex flex-row relative w-full h-auto opacity-100 rounded-[8px] bg-[#E1DFFD] text-justify mb-[8px] mt-[6px]`}
          >
            <textarea
              key={dataKey}
              id={dataKey}
              className="pt-[8px] pl-[12px] pb-[8px] pr-[12px] flex flex-wrap !text-[#011D58] bg-[#E1DFFD] font-karla font-medium text-[14px] leading-[1.2] tracking-[0px] w-full rounded-[8px] text-left"
              value={noteText || ''}
              placeholder="Add note..."
              rows={3}
              maxLength={500}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="flex flex-row items-center justify-between pl-5 pb-[4px] pr-5 gap-4">
              <button
                className="relative h-[16px] opacity-100 karla-font font-bold text-[14px] leading-[100%] tracking-[0px] text-[#3329EF]
"
                onClick={handleSaveNoteClick}
              >
                Save
              </button>
              <button
                className="relative h-[16px] opacity-100 karla-font font-bold text-[14px] leading-[100%] tracking-[0px] text-[#3329EF]"
                onClick={handleDeleteNoteClick}
              >
                Clear
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {noteText && (
            <div className="relative mb-[8px] mt-[6px] w-full rounded-[8px] bg-[#E1DFFD] px-3 py-3 opacity-100">
              <div className="flex items-start gap-2">
                {authorshipTooltip ? (
                  <Tooltip
                    title={authorshipTooltip}
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: '#616161',
                          color: '#FFFFFF',
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: '12px',
                          marginBottom: '6px !important',
                        },
                      },
                    }}
                  >
                    <span className="inline-flex h-[1.2em] shrink-0 items-center text-[14px]">
                      <button
                        type="button"
                        className="inline-flex h-[14px] w-[14px] items-center justify-center rounded-full border border-[#011D58] bg-transparent p-0 text-[9px] font-semibold leading-none text-[#011D58]"
                        aria-label={authorshipTooltip}
                      >
                        i
                      </button>
                    </span>
                  </Tooltip>
                ) : null}
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'm-0 w-full text-left karla-font font-normal text-[14px] leading-[1.2] tracking-[0px] !text-[#333333]',
                      !showMore && 'line-clamp-2',
                    )}
                    dangerouslySetInnerHTML={{
                      __html: noteText ? noteText.replace(/\n/g, '<br />') : '',
                    }}
                  />
                  {!editMode && note && (note.length > TruncateTextLength || note.split('\n').length > 2) && (
                    <div className="flex min-w-[100px] flex-row items-end justify-start pt-[3px]">
                      <button
                        className="flex cursor-pointer flex-row items-end text-[0.875rem] font-primary font-regular leading-[19px] text-textdark"
                        onClick={toggleShowMore}
                      >
                        {showMore ? 'View less' : 'View more'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
